import React, { useCallback, useMemo, useState } from 'react';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { Dialog } from '@headlessui/react';
import { Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { ExternalStreamlitApps } from 'libs/end-to-end/src/external-streamlit-apps';
import { OrganizationExternalStreamlitAppEntitlement } from 'libs/end-to-end/src/external-streamlit-apps';
import { OrganizationExternalStreamlitAppEntitlements } from 'libs/end-to-end/src/external-streamlit-apps';
import { GrantOrganizationStreamlitAppAccessRequest } from 'libs/end-to-end/src/external-streamlit-apps';
import { EmptyObject } from 'libs/end-to-end/src/generic';

interface OrganizationStreamlitAppsSectionProps {
  organizationId: string;
}

/**
 * OrganizationStreamlitAppsSection component
 *
 * This component displays and manages streamlit app entitlements for a specific organization.
 */
export const OrganizationStreamlitAppsSection: React.FC<
  OrganizationStreamlitAppsSectionProps
> = ({ organizationId }) => {
  const { userAuthenticatedApiCall } = useUser();

  const [isGrantModalOpen, setIsGrantModalOpen] = useState(false);
  const [selectedAppId, setSelectedAppId] = useState('');
  const [isGranting, setIsGranting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /**
   * Callback to get the URL for fetching streamlit apps for this organization
   */
  const getEntitlementsUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/streamlit-apps`;
  }, [organizationId]);

  /**
   * Hook for fetching and managing entitlements data
   */
  const entitlementsReadPattern = useSimpleReadPattern({
    getUrlFn: getEntitlementsUrlFn,
    canRead: true,
    responseSchema: OrganizationExternalStreamlitAppEntitlements,
  });

  /**
   * Callback to get the URL for fetching all streamlit apps
   */
  const getAllAppsUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/external-streamlit-apps`;
  }, []);

  /**
   * Hook for fetching all streamlit apps
   */
  const allAppsReadPattern = useSimpleReadPattern({
    getUrlFn: getAllAppsUrlFn,
    canRead: true,
    responseSchema: ExternalStreamlitApps,
  });

  /**
   * Function to refresh entitlements
   */
  const refreshEntitlements = useCallback(() => {
    if (
      entitlementsReadPattern.status === 'done' ||
      entitlementsReadPattern.status === 'error'
    ) {
      entitlementsReadPattern.reloadEntity();
    }
  }, [entitlementsReadPattern]);

  /**
   * Get available apps (apps not already granted)
   */
  const availableApps = useMemo(() => {
    if (
      allAppsReadPattern.status !== 'done' ||
      !('entity' in allAppsReadPattern) ||
      entitlementsReadPattern.status !== 'done' ||
      !('entity' in entitlementsReadPattern)
    ) {
      return [];
    }

    const grantedAppIds = new Set(
      entitlementsReadPattern.entity.map((e) => e.streamlitAppId),
    );
    return allAppsReadPattern.entity.filter(
      (app) => !grantedAppIds.has(app.id),
    );
  }, [allAppsReadPattern, entitlementsReadPattern]);

  /**
   * Get app name by ID
   */
  const getAppName = useCallback(
    (appId: string) => {
      if (
        allAppsReadPattern.status !== 'done' ||
        !('entity' in allAppsReadPattern)
      ) {
        return appId;
      }
      const app = allAppsReadPattern.entity.find((a) => a.id === appId);
      return app?.name || appId;
    },
    [allAppsReadPattern],
  );

  /**
   * Open grant modal
   */
  const openGrantModal = useCallback(() => {
    setSelectedAppId('');
    setErrorMessage(null);
    setIsGrantModalOpen(true);
  }, []);

  /**
   * Close grant modal
   */
  const closeGrantModal = useCallback(() => {
    setIsGrantModalOpen(false);
    setSelectedAppId('');
    setErrorMessage(null);
  }, []);

  /**
   * Async callback for granting access
   */
  const grantAccess = useAsyncCallback(
    async (isAlive) => {
      if (!isAlive() || !selectedAppId) return;

      setIsGranting(true);
      setErrorMessage(null);

      try {
        const request: GrantOrganizationStreamlitAppAccessRequest = {
          organizationId,
          streamlitAppId: selectedAppId,
        };

        const result = await userAuthenticatedApiCall({
          responseSchema: OrganizationExternalStreamlitAppEntitlement,
          url: `${getApiBaseUrl()}/external-streamlit-apps/grant-access`,
          requestOptions: {
            method: 'POST',
            body: JSON.stringify(request),
            headers: {
              'Content-Type': 'application/json',
            },
          },
        });

        if (!result) {
          throw new Error('Failed to grant access');
        }

        if (result.resultType === 'err') {
          throw new Error(
            (result.error.errorReason as string) || 'Failed to grant access',
          );
        }

        refreshEntitlements();
        closeGrantModal();
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to grant access',
        );
      } finally {
        setIsGranting(false);
      }
    },
    [
      selectedAppId,
      organizationId,
      userAuthenticatedApiCall,
      refreshEntitlements,
      closeGrantModal,
    ],
  );

  /**
   * Async callback for revoking access
   */
  const revokeAccess = useAsyncCallback(
    async (
      isAlive,
      entitlement: OrganizationExternalStreamlitAppEntitlement,
    ) => {
      if (!isAlive()) return;

      if (
        !confirm(
          'Are you sure you want to revoke access to this streamlit app?',
        )
      ) {
        return;
      }

      try {
        const result = await userAuthenticatedApiCall({
          responseSchema: EmptyObject,
          url: `${getApiBaseUrl()}/external-streamlit-apps/revoke-access?organizationId=${entitlement.organizationId}&streamlitAppId=${entitlement.streamlitAppId}`,
          requestOptions: {
            method: 'POST',
          },
        });

        if (!result) {
          throw new Error('Failed to revoke access');
        }

        if (result.resultType === 'err') {
          throw new Error(
            (result.error.errorReason as string) || 'Failed to revoke access',
          );
        }

        refreshEntitlements();
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to revoke access',
        );
      }
    },
    [userAuthenticatedApiCall, refreshEntitlements],
  );

  /**
   * Table column definitions for entitlements
   */
  const entitlementsColumnDefinitions = useMemo(
    () => [
      {
        id: 'app',
        header: () => <div>Streamlit App</div>,
        cell: ({
          item,
        }: {
          item: OrganizationExternalStreamlitAppEntitlement;
        }) => <div>{getAppName(item.streamlitAppId)}</div>,
      },
      {
        id: 'actions',
        header: () => <div>Actions</div>,
        cell: ({
          item,
        }: {
          item: OrganizationExternalStreamlitAppEntitlement;
        }) => (
          <Button
            behavior={{
              type: 'onClick',
              onClick: () => revokeAccess(item),
            }}
            text="Revoke Access"
            size="small"
            variant="outline"
          />
        ),
      },
    ],
    [getAppName, revokeAccess],
  );

  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-4">
        <S2Typography
          variant="headline-6-alt"
          translatedContent="Streamlit App Access"
        />
        <Button
          behavior={{
            type: 'onClick',
            onClick: openGrantModal,
          }}
          text="Grant Access to App"
          size="small"
        />
      </div>

      {errorMessage && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-700">
          {errorMessage}
        </div>
      )}

      <Table
        items={
          entitlementsReadPattern.status === 'done' &&
          'entity' in entitlementsReadPattern
            ? entitlementsReadPattern.entity
            : []
        }
        columnDefinitions={entitlementsColumnDefinitions}
        isLoading={entitlementsReadPattern.status === 'loading'}
        empty={
          <div className="text-center py-8 text-gray-500">
            No streamlit apps have been granted access yet
          </div>
        }
      />

      {/* Grant Access Modal */}
      <Transition appear show={isGrantModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeGrantModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Grant Streamlit App Access
                  </Dialog.Title>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Streamlit App
                    </label>
                    {allAppsReadPattern.status === 'loading' && (
                      <p className="text-sm text-gray-500 mt-2">
                        Loading available apps...
                      </p>
                    )}
                    {allAppsReadPattern.status === 'error' && (
                      <p className="text-sm text-red-600 mt-2">
                        Error loading apps. Please try again.
                      </p>
                    )}
                    {allAppsReadPattern.status === 'done' &&
                      'entity' in allAppsReadPattern && (
                        <>
                          {availableApps.length > 0 ? (
                            <select
                              value={selectedAppId}
                              onChange={(e) => setSelectedAppId(e.target.value)}
                              className="form-select block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                            >
                              <option value="">Select an app...</option>
                              {availableApps.map((app) => (
                                <option key={app.id} value={app.id}>
                                  {app.name} ({app.streamlitSlug})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <p className="text-sm text-gray-500 mt-2">
                              All available streamlit apps have been granted
                              access.
                            </p>
                          )}
                        </>
                      )}
                  </div>

                  <div className="mt-6 flex justify-end gap-2">
                    <Button
                      behavior={{
                        type: 'onClick',
                        onClick: closeGrantModal,
                      }}
                      text="Cancel"
                      variant="outline"
                    />
                    <Button
                      behavior={{
                        type: 'onClick',
                        onClick: grantAccess,
                      }}
                      text="Grant Access"
                      disabled={isGranting || !selectedAppId}
                    />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};
